import type { PublicEvent, PdfDocumentDefinition, PdfPlacement } from '../../shared/types';
import type { PdfPageGeometry } from '../../shared/pdf/geometry';
import { PDF_LIMITS, validatePdfResources } from '../../shared/pdf/limits';
import { API, ApiError, api } from './api';
import { reportingFetch } from './clientErrors';

export interface PdfAssetMetadata {
  assetId: string;
  templateSha256: string;
  byteLength: number;
  pages: PdfPageGeometry[];
  nativeFields: Array<Pick<PdfPlacement, 'kind' | 'pageIndex' | 'rect' | 'nativeFieldName'>>;
}
export function attachPdf(asset: PdfAssetMetadata, title: string): PdfDocumentDefinition {
  return { id: crypto.randomUUID(), title, assetId: asset.assetId, templateSha256: asset.templateSha256,
    questions: [], placements: [] };
}
export async function uploadBlankPdf(file: File, eventId: string): Promise<PdfAssetMetadata> {
  if (!file.size || file.size > PDF_LIMITS.sourceBytes) throw new ApiError(413, 'too_large');
  const form = new FormData(); form.set('file', file);
  return api.postForm<PdfAssetMetadata>(`/events/${eventId}/pdf-assets`, form);
}
export function pdfActionError(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Could not load or save this form. Check your connection and try again.';
  const messages: Record<string, string> = {
    revision_conflict: 'This event has changed. Refresh the event and review your recovered answers before continuing.',
    closed: 'Registration has closed. Contact the event organizer.',
    invalid_pdf_configuration: 'Review the form titles, input labels, signature policies and source mappings before publishing.',
    native_target: 'A native field no longer matches its widgets. Remove and re-add its native placements.',
    placement_bounds: 'Move or resize the indicated placements so they fit completely inside their PDF pages.',
    template_mismatch: 'The blank source does not match this document. Replace it and review the placements.',
    pdf_configuration_required: 'Reload this event to preserve its saved additional forms.',
    too_large: 'Choose a nonempty blank PDF no larger than 5 MB.',
    invalid_template: 'Use a readable, unencrypted blank PDF.',
    invalid_geometry: 'Export a standard PDF with supported page dimensions and rotation.',
    unsupported_native_fields: 'This PDF has unsupported form controls. Export a flat blank PDF and try again.',
    resource_limit: 'Use a simpler blank PDF with smaller images.',
    rate_limited: 'Too many uploads. Wait an hour before trying again.',
    template_unavailable: 'The blank source is unavailable. Retry loading it or replace it with a new blank PDF.',
    draft_conflict: 'This event changed in another tab. Resolve the saved draft conflict before continuing.',
    not_found: 'The event or blank source is unavailable. Reload the event or replace the source.',
    asset_limit_or_event_removed: 'The event was removed or your account has reached its blank-source limit.',
  };
  return messages[error.code] ?? 'Could not save this blank PDF. Retry, or export a simpler PDF.';
}

export interface OrganizerPdfSource { bytes: Uint8Array; pages: PdfPageGeometry[]; nativeFields: PdfAssetMetadata['nativeFields'] }
export async function loadOrganizerPdf(eventId: string, document: PdfDocumentDefinition, signal: AbortSignal): Promise<OrganizerPdfSource> {
  const response = await reportingFetch(`${API}/events/${eventId}/pdf-assets/${document.assetId}`, { credentials: 'same-origin', cache: 'no-store', signal });
  return inspectPdfResponse(response, document, signal);
}

/** Only the current published event reference authorizes participant template access. */
export async function loadParticipantPdf(event: PublicEvent, document: PdfDocumentDefinition, signal: AbortSignal): Promise<OrganizerPdfSource> {
  if (!event.eventId || !event.publishedRevisionId) throw new ApiError(409, 'revision_conflict');
  const eventId = event.eventId, revisionId = event.publishedRevisionId;
  // Stopped by the caller or a 30 second deadline, whichever comes first. (AbortSignal.any would do this, but Safari
  // has it only from 17.4.)
  const deadline = AbortSignal.timeout(30_000), controller = new AbortController(), bounded = controller.signal;
  const stops = [signal, deadline].map(from => { const stop = () => controller.abort(from.reason); if (from.aborted) stop(); else from.addEventListener('abort', stop, { once: true }); return () => from.removeEventListener('abort', stop); });
  try {
    const response = await abortable(() => reportingFetch(`${API}/public/events/${encodeURIComponent(eventId)}/documents/${encodeURIComponent(document.id)}/template?revisionId=${encodeURIComponent(revisionId)}`, { credentials: 'omit', cache: 'no-store', signal: bounded }), bounded);
    return await inspectPdfResponse(response, document, bounded);
  } finally { for (const remove of stops) remove(); }
}
async function inspectPdfResponse(response: Response, document: PdfDocumentDefinition, signal: AbortSignal): Promise<OrganizerPdfSource> {
  if (!response.ok) throw new ApiError(response.status, (await abortable(() => response.json(), signal).catch(() => ({}))).error ?? 'template_unavailable');
  if (!response.body) throw new ApiError(503, 'template_unavailable');
  const reader = response.body.getReader(), chunks: Uint8Array[] = []; let size = 0;
  try {
    while (true) { const item = await abortable(() => reader.read(), signal); if (item.done) break; size += item.value.length; if (size > PDF_LIMITS.sourceBytes) throw new ApiError(413, 'too_large'); chunks.push(item.value); }
  } catch (error) { void reader.cancel().catch(() => {}); throw error; }
  finally { reader.releaseLock(); }
  const bytes = new Uint8Array(size); let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
  const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', bytes.slice().buffer))].map(b => b.toString(16).padStart(2, '0')).join('');
  if (hash !== document.templateSha256) throw new ApiError(503, 'template_unavailable');
  const [{ loadPdfTemplate, inspectPageGeometry }, { inspectNativePdfFields, nativePlacementSuggestions }] = await Promise.all([
    import('../../shared/pdf/inspect'), import('../../shared/pdf/nativeFields'),
  ]);
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  const pdf = await loadPdfTemplate(bytes), pages = inspectPageGeometry(pdf); validatePdfResources(pdf, pages);
  return { bytes, pages, nativeFields: nativePlacementSuggestions(inspectNativePdfFields(pdf, pages)) };
}

function abortable<T>(operation: () => Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise<T>((resolve, reject) => {
    const abort = () => { signal.removeEventListener('abort', abort); reject(signal.reason); };
    signal.addEventListener('abort', abort, { once: true });
    operation().then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
  });
}
